// Catalog content model.
//
// Shopify carries no genre metadata (every book is productType "Books - New")
// and its descriptions are messy ("Format... Product details Author:..."), so
// the editorial layer lives here, keyed by Shopify product `handle`:
//   - genre (canonical code from the submission taxonomy in forms/SubmissionForm.js)
//   - target audience (TRADE / FAMILY / YA)
//   - author + a short authored hook
//   - external-format links (Kindle -> Amazon, Audiobook -> Audible)
//
// Later this migrates to Shopify product tags (admin task); the Catalog reads
// through the helpers below so that swap is localized.

// Canonical genre code -> short display label + genre-dot CSS variable.
// Codes match the <MenuItem value=...> list in forms/SubmissionForm.js.
export const GENRES = {
  FICTION_HORROR:            { label: 'Horror',        dot: 'var(--nf-g-horror)' },
  FICTION_ROMANCE_GENERAL:   { label: 'Romance',       dot: 'var(--nf-g-romance)' },
  FICTION_THRILLERS_GENERAL: { label: 'Thriller',      dot: 'var(--nf-g-thriller)' },
  FICTION_GENERAL:           { label: 'Fiction',       dot: 'var(--nf-g-fiction)' },
  'FICTION_SHORT STORIES (SINGLE AUTHOR)': { label: 'Short Stories', dot: 'var(--nf-g-shortstories)' },
  FICTION_FANTASY_GENERAL:   { label: 'Fantasy',       dot: 'var(--nf-g-fantasy)' },
};

export const AUDIENCE = {
  TRADE:  { label: 'Adult' },
  FAMILY: { label: 'Family' },
  YA:     { label: 'YA' },
};

// Per-book editorial content, keyed by Shopify handle.
// `hook` = one-line grid tagline; `blurb` = fuller synopsis for the detail
// lightbox; `meta` = publication details for the lightbox.
export const BOOK_CONTENT = {
  'never-sleep-again': {
    genre: 'FICTION_HORROR',
    audience: 'TRADE',
    author: 'Jeff Provine',
    hook: 'A modern master of the campfire tale conjures chills, goosebumps, and the shadowy truths hiding behind every urban legend.',
    blurb: "Monsters lurk in the dark. Horrors go bump in the night. Secrets wait to be found. Jeff Provine's dark imagination brings a modern take on the classic scary story — from urban legends to the ghosts that haunt machines — and the shadowy truths behind each one. Whether it's the strangest thing you've ever heard or the thing you always feared was true, one thing's certain: after these tales, you'll never sleep again.",
    badge: 'Latest',
    meta: { format: 'US Trade Paperback', pages: 164, isbn: '978-1-963957-05-1', published: 'October 2024' },
  },
  'this-isnt-the-end': {
    genre: 'FICTION_GENERAL',
    audience: 'YA',
    author: 'Louis Alvarez',
    hook: 'A girl who lives inside fairy tales is offered eternal life — if she is willing to outlive everyone she has ever loved.',
    blurb: "The world is magical for a girl whose life is lived in stories and fairy tales. Coming to terms with the death of her beloved father, fourteen-year-old Evie Liddell still hopes to find that magic — until she is diagnosed with the same cancer that took him, and begins to wonder why she should go on. Then an enigmatic boy appears at her window, telling her that magic is real and eternal life can be hers. Of all her questions, the biggest is this: does she want to outlive everyone she has ever loved?",
    meta: { format: 'Paperback', pages: 140, isbn: '978-1-963957-04-4', published: 'August 2024' },
  },
  'lucky': {
    genre: 'FICTION_THRILLERS_GENERAL',
    audience: 'TRADE',
    author: 'Ian Klink',
    hook: 'A kingpin. The best billiards player in River Valley. And a single game where the loser loses far more than the match.',
    blurb: "When kingpin Johnny Nolin wants the best billiards player in all of River Valley working for him, one name keeps coming up: Michael Andreavich — better known as Lucky. But that's not the only name Nolin hears, and he's not the kind of man to trust luck. So he sets up the game of a lifetime. The winner gets to work for him. The loser? Well, he loses a good deal more than just the game.",
    meta: { format: 'Paperback', pages: 102, isbn: '978-1-963957-03-7', published: 'June 2024' },
  },
  'the-repercussions-of-us': {
    genre: 'FICTION_ROMANCE_GENERAL',
    audience: 'TRADE',
    author: 'Andrea Casanova',
    hook: 'A new city, no friends, and a too-good-to-be-true tattoo artist. In her search for love, Rain finds something harder to hold — herself.',
    blurb: "When her determination to move away from home smacks face-first into the reality of starting over with no friends and no boyfriend, Rain Rivas has to figure out who she is — fast. A tattoo seems like a great place to start, right up until things get complicated with the too-good-to-be-true tattoo artist. What begins as friendship becomes a fracture, and Rain soon learns that not every new beginning is a fresh start. In her search for friendship, family, and love, she finds something far more important: herself.",
    meta: { format: 'Paperback', pages: 181, isbn: '978-1-963957-02-0', published: 'April 2024' },
  },
  'fortunate-son': {
    genre: 'FICTION_GENERAL',
    audience: 'TRADE',
    author: 'Dennis J. Kafalas',
    hook: 'Summer of 1992, small-town America. A hot-headed dropout discovers that family is made of far more than blood.',
    blurb: "For Moe Richards, dropping out of high school feels like an inevitable way to deal with the crushing weight of anxiety. It leaves him with freedom, peace… and no prospects. It's the summer of '92, and small-town America along the banks of the Faithful River expects men to find a job — whether or not they can find themselves. Raised without a father, Moe's discovery that family is more than blood will change his life forever, in a novel of betrayal, sacrifice, trust, and honor.",
    meta: { format: 'Paperback', pages: 206, isbn: '978-1-963957-01-3', published: 'April 2024' },
  },
  'the-living-episode-1-war-stories': {
    genre: 'FICTION_HORROR',
    audience: 'TRADE',
    author: 'Newfangle Collective',
    hook: 'When the world ends, an unlikely band of misfits endures. In a land owned by the dead, they are the Living.',
    blurb: "When the world as we know it ends, it's an unlikely band of misfits who survive. Along with Shepherd, the enigmatic cowboy who brings them together, they all share one thing in common — in a world populated by the dead, they are the Living. A gorgeously assembled collection of comedic horror short stories, Episode 1: War Stories will have you on the edge of your seat when it doesn't have you laughing.",
    format: 'Serial · Magazine',
    meta: { format: 'Magazine', pages: 56, issn: '2836-3302', published: 'February 2023' },
  },
  // The Ingénue — flagship work; four Shopify products share this content.
  'the-ingenue-paperback-v2': {
    genre: 'FICTION_HORROR',
    audience: 'TRADE',
    author: 'William Wager',
    hook: 'In Cincinnati, everything the undead touch turns to ash. When Sorcha Mackenzie vanishes on Halloween night, the four people who love her most descend into the dark to bring her back.',
    blurb: "“Stay away from him. Stay away from all of my kind, actually. Everything we touch turns to ash.” In the city of Cincinnati, everything the undead touch turns to ash — including the lives of the mortals around them. One of these mortals is Sorcha Mackenzie, a young woman who disappears on Halloween night, leaving the four people who love her most desperate to find her. Their search takes them into the midst of the grudges and machinations of the undead, where getting her back might mean defeating not just the supernatural, but also the demons of their own making.",
    meta: { format: 'Paperback', pages: 300, isbn: '978-1-7359595-0-4', published: 'October 2020' },
  },
  'the-ingenue-hardback': {
    genre: 'FICTION_HORROR', audience: 'TRADE', author: 'William Wager',
    hook: 'The hardback edition of the flagship Newfangle novel.',
  },
  'the-ingenue-kindle-edition': {
    genre: 'FICTION_HORROR', audience: 'TRADE', author: 'William Wager', hook: '',
  },
  'the-ingenue-audiobook': {
    genre: 'FICTION_HORROR', audience: 'TRADE', author: 'William Wager', hook: '',
  },
};

// Optional merch copy, keyed by handle. `kind` is the small eyebrow label.
export const MERCH_CONTENT = {
  'the-np-t':                { kind: 'Apparel' },
  'newfangle-press-t-shirt': { kind: 'Apparel' },
  'womens-racerback-tank':   { kind: 'Apparel' },
  'newfangle-press-glass':   { kind: 'Drinkware · 16 oz' },
};

// External / read-elsewhere formats (priced $0 in Shopify; never hit the cart).
// URLs lifted from Product1.js / ShopifyBuyNow.js.
export const EXTERNAL_LINKS = {
  'the-ingenue-kindle-edition': {
    label: 'Kindle',
    url: 'https://www.amazon.com/gp/r.html?C=2Z24ENUUAEWUK&K=3AIIK8M6AH4S9&M=urn:rtn:msg:202010230347527607674f04834c2c93a2f9494730p0na&R=HR8XCNWZMRAQ&T=C&U=http%3A%2F%2Fwww.amazon.com%2Fdp%2FB08LP653NR%3Fref_%3Dpe_3052080_276849420&H=AWK8AIUCNMAPPWF9OGQPD08LMUIA&ref_=pe_3052080_276849420',
  },
  'the-ingenue-audiobook': {
    label: 'Audiobook',
    url: 'https://www.audible.com/pd/B09N4QZH3W',
  },
};

// Flagship work shown in the hero: the primary "buy here" product plus the
// set of format handles grouped under it.
export const FLAGSHIP = {
  primaryHandle: 'the-ingenue-paperback-v2',
  title: 'The Ingénue',
  eyebrow: 'Flagship title · The Cincinnati By Night Chronicle',
  formatHandles: [
    'the-ingenue-paperback-v2',
    'the-ingenue-hardback',
    'the-ingenue-kindle-edition',
    'the-ingenue-audiobook',
  ],
};

// ---- helpers ----

export function contentFor(handle) {
  return BOOK_CONTENT[handle] || null;
}

export function genreLabel(code) {
  return (GENRES[code] && GENRES[code].label) || 'Fiction';
}

export function genreDot(code) {
  return (GENRES[code] && GENRES[code].dot) || 'var(--nf-muted)';
}

export function isExternalFormat(handle) {
  return Boolean(EXTERNAL_LINKS[handle]);
}

// True for products that belong to the flagship work (so the grid can skip
// them — they live in the hero instead).
export function isFlagshipFormat(handle) {
  return FLAGSHIP.formatHandles.includes(handle);
}
